// src/App.jsx - Исправленная версия БЕЗ дублирования Layout
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Loader from './components/common/Loader';

// icons
import './icons';
// Компоненты аутентификации
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthRedirectRoute from './components/auth/AuthRedirectRoute';

// Утилиты
import { startCacheCleanup } from './utils/cacheCleanup';

// ===== DOCS =====
const DocsPage = React.lazy(() => import('./pages/DocsPage'));

// ===== ОСНОВНЫЕ СТРАНИЦЫ =====
const HomePage = React.lazy(() => import('./pages/HomePage'));
const VideoAnalysisPage = React.lazy(() => import('./pages/VideoAnalysisPage'));
const ChannelAnalysisPage = React.lazy(() => import('./pages/ChannelAnalysisPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// ===== СТРАНИЦЫ КАНАЛОВ =====
const ChannelDiscoveryPage = React.lazy(() => import('./pages/ChannelDiscoveryPage'));
const ChannelComparisonPage = React.lazy(() => import('./pages/ChannelComparisonPage'));
const NicheInsightsPage = React.lazy(() => import('./pages/NicheInsightsPage'));

// ===== СТРАНИЦЫ ВИДЕО =====
const VideoDiscoveryPage = React.lazy(() => import('./pages/VideoDiscoveryPage'));
const VideoComparisonPage = React.lazy(() => import('./pages/VideoComparisonPage'));
const VideoInfoPage = React.lazy(() => import('./pages/VideoInfoPage'));

// ===== RESEARCH СТРАНИЦЫ =====
const YTDashboardPage = React.lazy(() => import('./pages/YTDashboardPage'));
const MostViewedPage = React.lazy(() => import('./pages/MostViewedPage'));
const KeywordsPage = React.lazy(() => import('./pages/KeywordsPage'));
const MonetizationPage = React.lazy(() => import('./pages/MonetizationPage'));

// ===== MARKETPLACE СТРАНИЦА =====
const MarketPlacePage = React.lazy(() => import('./pages/MarketPlacePage'));

// ===== СТРАНИЦЫ АУТЕНТИФИКАЦИИ =====
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const ForgotPassword = React.lazy(() => import('./pages/auth/ForgotPassword'));
const PasswordReset = React.lazy(() => import('./pages/auth/PasswordReset'));
const VerifyEmail = React.lazy(() => import('./pages/auth/VerifyEmail'));
const ResendVerification = React.lazy(() => import('./pages/auth/ResendVerification'));
const Logout = React.lazy(() => import('./pages/auth/Logout'));

// ===== ПОЛЬЗОВАТЕЛЬСКИЕ СТРАНИЦЫ =====
const UserProfile = React.lazy(() => import('./pages/profile/UserProfile'));
const SubscriptionCheckout = React.lazy(() => import('./pages/subscription/SubscriptionCheckout'));
const SubscriptionReturn = React.lazy(() => import('./pages/subscription/SubscriptionReturn'));
const SubscriptionExists = React.lazy(() => import('./pages/subscription/SubscriptionExists'));
const PricingPage = React.lazy(() => import('./pages/subscription/PricingPage'));

const App = () => {
  // Автоочистка устаревшего кэша при запуске приложения
  useEffect(() => {
    const stopCleanup = startCacheCleanup();
    return stopCleanup; // Остановка при размонтировании приложения
  }, []);

  // ✅ BrowserRouter уже есть в index.jsx - убираем дублирование
  return (
    <Layout>
      <Suspense fallback={
        <div className="suspense-loader" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}>
          <Loader size="lg" />
        </div>
      }>
        <Routes>
            {/* ===== ПУБЛИЧНЫЕ МАРШРУТЫ ===== */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/marketplace" element={<MarketPlacePage />} />
            
            {/* ===== АУТЕНТИФИКАЦИЯ ===== */}
            <Route path="/login" element={<AuthRedirectRoute><Login /></AuthRedirectRoute>} />
            <Route path="/register" element={<AuthRedirectRoute><Register /></AuthRedirectRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email/:uidb64/:token" element={<VerifyEmail />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/reset-password/:uidb64/:token" element={<PasswordReset />} />
            <Route path="/password-reset/:uidb64/:token" element={<PasswordReset />} />
            <Route path="/docs" element={<DocsPage />} />
            
            {/* ===== DISCOVERY СТРАНИЦЫ - ПУБЛИЧНЫЕ ===== */}
            <Route path="/channel-discovery" element={<ChannelDiscoveryPage />} />
            <Route path="/video-discovery" element={<VideoDiscoveryPage />} />
            <Route path="/video-info/:videoId" element={<VideoInfoPage />} />
          
            {/* ===== ЗАЩИЩЕННЫЕ МАРШРУТЫ ===== */}
            <Route element={<ProtectedRoute />}>
              {/* Analysis страницы */}
              <Route path="/video-analysis" element={<VideoAnalysisPage />} />
              <Route path="/channel-analysis" element={<ChannelAnalysisPage />} />
              <Route path="/logout" element={<Logout />} />
              
              {/* Сравнения и премиум функции */}
              <Route path="/channel-comparison" element={<ChannelComparisonPage />} />
              <Route path="/niche-insights/:category/:subcategory?" element={<NicheInsightsPage />} />
              <Route path="/video-comparison" element={<VideoComparisonPage />} />

              {/* Research страницы */}
              <Route path="/research/dashboard" element={<YTDashboardPage />} />
              <Route path="/research/most-viewed" element={<MostViewedPage />} />
              <Route path="/research/keywords" element={<KeywordsPage />} />
              <Route path="/research/monetization" element={<MonetizationPage />} />

              {/* Пользовательские страницы */}
              <Route path="/profile" element={<UserProfile />} />
              
              {/* Подписки */}
              <Route path="/subscription" element={<SubscriptionCheckout />} />
              <Route path="/subscription/return" element={<SubscriptionReturn />} />
              <Route path="/subscription/exists" element={<SubscriptionExists />} />
            </Route>
            
            {/* ===== ОШИБКИ ===== */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    );
  };

export default App;